import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, machinePhotoSrc } from "../lib/api";
import { MACHINE_STATUSES } from "../lib/constants";

const inputCls =
  "w-full h-12 px-4 rounded-xl border border-zinc-200 bg-white text-base placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 transition-shadow";

export default function MachineForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({
    name: "", manufacturer: "", model: "", serial_number: "",
    supplier: "", service_application: "", warranty_until: "", year: "", location_id: "none", status: "operativa", description: "",
  });
  const [newLocation, setNewLocation] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/locations").then(({ data }) => setLocations(data));
    if (isEdit) {
      api.get(`/machines/${id}`).then(({ data }) => {
        setForm({
          name: data.name || "", manufacturer: data.manufacturer || "",
          model: data.model || "", serial_number: data.serial_number || "",
          supplier: data.supplier || "", service_application: data.service_application || "",
          warranty_until: data.warranty_until || "",
          year: data.year || "", location_id: data.location_id || "none",
          status: data.status || "operativa", description: data.description || "",
        });
        setPhotoPreview(machinePhotoSrc(data));
      });
    }
  }, [id, isEdit]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      let locationId = form.location_id === "none" ? "" : form.location_id;
      if (!locationId && newLocation.trim()) {
        const { data } = await api.post("/locations", { name: newLocation.trim() });
        locationId = data.id;
      }
      const fd = new FormData();
      Object.entries({ ...form, location_id: locationId }).forEach(([k, v]) => fd.append(k, v));
      if (photoFile) fd.append("photo", photoFile);

      const { data } = isEdit
        ? await api.patch(`/machines/${id}`, fd)
        : await api.post("/machines", fd);
      toast.success(isEdit ? "Máquina actualizada" : "Máquina creada");
      navigate(`/app/machines/${data.id}`);
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto animate-fade-up" data-testid="machine-form-page">
      <Link
        to={isEdit ? `/app/machines/${id}` : "/app"}
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors mb-6"
        data-testid="back-link"
      >
        <ArrowLeft size={15} /> Volver
      </Link>

      <h1 className="font-heading text-3xl font-bold tracking-tighter mb-8">
        {isEdit ? "Editar máquina" : "Nueva máquina"}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] font-medium text-zinc-500 mb-2">
            Nombre de la máquina *
          </label>
          <input
            className={inputCls}
            placeholder='Ej. "Inyectora 01"'
            value={form.name}
            onChange={set("name")}
            required
            autoFocus
            data-testid="machine-name-input"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] font-medium text-zinc-500 mb-2">Fabricante</label>
            <input className={inputCls} placeholder="BMB" value={form.manufacturer} onChange={set("manufacturer")} data-testid="machine-manufacturer-input" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] font-medium text-zinc-500 mb-2">Modelo</label>
            <input className={inputCls} placeholder="eKW 220" value={form.model} onChange={set("model")} data-testid="machine-model-input" />
          </div>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-[0.15em] font-medium text-zinc-500 mb-2">Proveedor</label>
          <input className={inputCls} placeholder="MTP" value={form.supplier} onChange={set("supplier")} data-testid="machine-supplier-input" />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-[0.15em] font-medium text-zinc-500 mb-2">Servicio / aplicación</label>
          <input className={inputCls} placeholder="Ej. Refrigeración de línea, anillo, entrada…" value={form.service_application} onChange={set("service_application")} data-testid="machine-service-application-input" />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-[0.15em] font-medium text-zinc-500 mb-2">Garantía hasta</label>
          <input className={inputCls} type="date" value={form.warranty_until} onChange={set("warranty_until")} data-testid="machine-warranty-input" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] font-medium text-zinc-500 mb-2">Número de serie</label>
            <input className={inputCls} value={form.serial_number} onChange={set("serial_number")} data-testid="machine-serial-input" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] font-medium text-zinc-500 mb-2">Año</label>
            <input className={inputCls} inputMode="numeric" value={form.year} onChange={set("year")} data-testid="machine-year-input" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] font-medium text-zinc-500 mb-2">Ubicación</label>
            <select
              className={inputCls}
              value={form.location_id}
              onChange={set("location_id")}
              data-testid="machine-location-select"
            >
              <option value="none">Sin ubicación</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
              <option value="">＋ Nueva ubicación…</option>
            </select>
            {form.location_id === "" && (
              <input
                className={`${inputCls} mt-3`}
                placeholder="Nombre de la nueva ubicación"
                value={newLocation}
                onChange={(e) => setNewLocation(e.target.value)}
                data-testid="machine-new-location-input"
              />
            )}
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] font-medium text-zinc-500 mb-2">Estado</label>
            <select className={inputCls} value={form.status} onChange={set("status")} data-testid="machine-status-select">
              {MACHINE_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-[0.15em] font-medium text-zinc-500 mb-2">Foto principal</label>
          <label
            className="flex items-center gap-4 p-4 rounded-2xl border border-dashed border-zinc-300 bg-white cursor-pointer hover:border-zinc-400 transition-colors"
            data-testid="machine-photo-upload"
          >
            {photoPreview ? (
              <img src={photoPreview} alt="Vista previa" className="h-16 w-16 rounded-xl object-cover" />
            ) : (
              <span className="h-16 w-16 rounded-xl bg-zinc-100 flex items-center justify-center text-zinc-400">
                <ImagePlus size={22} />
              </span>
            )}
            <span className="text-sm text-zinc-500">
              {photoPreview ? "Cambiar foto" : "Toca para añadir una foto"}
            </span>
            <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} data-testid="machine-photo-input" />
          </label>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-[0.15em] font-medium text-zinc-500 mb-2">Descripción / notas</label>
          <textarea
            className="w-full min-h-[100px] px-4 py-3 rounded-xl border border-zinc-200 bg-white text-base placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 transition-shadow resize-none"
            value={form.description}
            onChange={set("description")}
            data-testid="machine-description-input"
          />
        </div>

        {error && (
          <p className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-4 py-2.5" data-testid="machine-form-error">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="machine-submit-button"
          className="w-full h-13 py-3.5 rounded-full bg-zinc-900 text-white font-medium hover:bg-zinc-800 active:scale-[0.98] transition-[background-color,transform] duration-200 disabled:opacity-50"
        >
          {loading ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear máquina"}
        </button>
      </form>
    </div>
  );
}
